import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { startLatencyProbe } from '../../../services/latencyProbe';
import { subscribeToLatencySamples } from '../../../services/networkLatencyObserver';
import { startResourceTimingObserver } from '../../../services/resourceTimingObserver';
import { computeDegradedCandidate } from '../helpers/computeDegradedCandidate';
import { getNetworkConnection } from '../helpers/getNetworkConnection';
import { readNetworkSignals } from '../helpers/readNetworkSignals';
import {
  ENTER_DEGRADED_DELAY_MS,
  EXIT_DEGRADED_DELAY_MS,
} from '../NetworkStatusProvider.constants';
import {
  NETWORK_STATUS,
  NetworkQualityContext,
  NetworkStatus,
} from '../NetworkStatusProvider.types';

export interface UseNetworkQualityReturnValue {
  networkStatus: NetworkStatus;
  networkContextRef: MutableRefObject<NetworkQualityContext>;
}

export const useNetworkQuality = (): UseNetworkQualityReturnValue => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>(
    NETWORK_STATUS.GOOD,
  );

  const networkContextRef = useRef<NetworkQualityContext>({});

  const statusRef = useRef<NetworkStatus>(networkStatus);

  statusRef.current = networkStatus;

  const enterTimerRef = useRef<number | undefined>(undefined);
  const exitTimerRef = useRef<number | undefined>(undefined);

  const clearEnterTimer = useCallback(() => {
    if (enterTimerRef.current !== undefined) {
      window.clearTimeout(enterTimerRef.current);
      enterTimerRef.current = undefined;
    }
  }, []);

  const clearExitTimer = useCallback(() => {
    if (exitTimerRef.current !== undefined) {
      window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = undefined;
    }
  }, []);

  const evaluate = useCallback(() => {
    const signals = readNetworkSignals();

    networkContextRef.current = {
      effectiveType: signals.effectiveType,
      rtt: signals.rtt,
      downlink: signals.downlink,
      rollingAverageLatencyMs: signals.rollingAverageLatencyMs,
      hasNetworkMeasurement: signals.hasNetworkMeasurement,
      latencySource: signals.latencySource,
    };

    if (!signals.isOnline) {
      clearEnterTimer();
      clearExitTimer();
      setNetworkStatus(NETWORK_STATUS.OFFLINE);

      return;
    }

    const isDegradedCandidate = computeDegradedCandidate(signals);
    const currentStatus = statusRef.current;

    if (currentStatus === NETWORK_STATUS.OFFLINE) {
      clearEnterTimer();
      clearExitTimer();
      setNetworkStatus(
        isDegradedCandidate ? NETWORK_STATUS.DEGRADED : NETWORK_STATUS.GOOD,
      );

      return;
    }

    if (isDegradedCandidate) {
      clearExitTimer();

      if (
        currentStatus === NETWORK_STATUS.GOOD &&
        enterTimerRef.current === undefined
      ) {
        enterTimerRef.current = window.setTimeout(() => {
          enterTimerRef.current = undefined;
          setNetworkStatus(NETWORK_STATUS.DEGRADED);
        }, ENTER_DEGRADED_DELAY_MS);
      }

      return;
    }

    clearEnterTimer();

    if (
      currentStatus === NETWORK_STATUS.DEGRADED &&
      exitTimerRef.current === undefined
    ) {
      exitTimerRef.current = window.setTimeout(() => {
        exitTimerRef.current = undefined;
        setNetworkStatus(NETWORK_STATUS.GOOD);
      }, EXIT_DEGRADED_DELAY_MS);
    }
  }, [clearEnterTimer, clearExitTimer]);

  useEffect(() => {
    evaluate();

    const connection = getNetworkConnection();

    window.addEventListener('online', evaluate);
    window.addEventListener('offline', evaluate);
    connection?.addEventListener?.('change', evaluate);
    const unsubscribe = subscribeToLatencySamples(evaluate);
    const disconnectResourceTimingObserver = startResourceTimingObserver();
    const stopLatencyProbe = startLatencyProbe();

    return () => {
      window.removeEventListener('online', evaluate);
      window.removeEventListener('offline', evaluate);
      connection?.removeEventListener?.('change', evaluate);
      unsubscribe();
      disconnectResourceTimingObserver();
      stopLatencyProbe();
      clearEnterTimer();
      clearExitTimer();
    };
  }, [evaluate, clearEnterTimer, clearExitTimer]);

  return {
    networkStatus,
    networkContextRef,
  };
};
